import './PageSidePanel.scss';

import GenericFormControl from '@components/Common/GenericFormControl';
import { DenominatorLabels, EXCLUDED_DECKLINES_FOR_DENOMINATORS } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import { useTOCContext } from '@context/TOCContext';
import { PermissionLevel, useUserContext } from '@context/UserContext';
import ConfirmDialog from '@deckManagement/components/Common/Dialogs/ConfirmDialog';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import TableOfContentsSelector from '@deckManagement/components/Common/SidePanel/TableOfContentsSelector';
import KPILibrary from '@deckManagement/components/KPILibrary/KPILibrary';
import CalculatedKPIPanel from '@deckManagement/components/Page/SidePanel/CalculatedKPIPanel';
import useTocSelectorConfig from '@deckManagement/hooks/useTocSelectorConfig';
import useVariantDriverSync from '@deckManagement/hooks/useVariantDriverSync';
import { KPILibraryHandle, KPILibraryView } from '@deckManagement/types/KPILibrary.types';
import { ViewType } from '@deckManagement/types/TableOfContentsSelector.types';
import { Alert, Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  CalcExpression,
  CalcExpressionInput,
  CalculatedKPIValues,
  DatasetSource,
  GalaxyKPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetPageLayout } from '@services/api/deckManagement/hooks/useGetPageLayout';
import { useGetVariantsForRootKPI } from '@services/api/deckManagement/hooks/useKPIVariants';
import { entityKeyAsId } from '@utils/deckHelpers';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import {
  asGalaxyDrivers,
  asKPIDrivers,
  datasetSourceFromDrivers,
  EMPTY_KPI_DRIVERS,
  generateBaseKpiId,
  hasDriverMismatch,
  readPageDrivers,
  resolveDefaultVariantDataset,
} from '@utils/kpiDriverHelpers';
import { isEmpty, isNil, isNull } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LinkedKPIChangeItem } from 'types/DecklineNameInput.types';
import { EnhancedKPIVariant } from 'types/KPILibraryContext.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import {
  ADMIN_TAB,
  AnyKPIDrivers,
  DriverType,
  KpiDimension,
  PageSettingsView,
  PageSidePanelDropDown,
  PanelTab,
} from 'types/PageSidePanel.types';

import DecklineFormattingOptions from './DecklineFormattingOptions';
import DecklineNameInput from './DecklineNameInput';
import PageAdminPanel from './PageAdminPanel';
import PageSettingsPanel from './PageSettingsPanel';
import useSubtotalHelper from './SubtotalHelper';
import VariantOverridesDisplay from './VariantOverridesDisplay';

/**
 * Side panel component for a Page
 * Displays contextual options with User actions, for the Page view
 */
const PageSidePanel = () => {
  const { deckId } = useParams() as { deckId: string };
  const { rootNodes } = useDeckContext();
  const { getTocItemsByGroup } = useTOCContext();
  const {
    selectedDeckline,
    updatePageDeckline,
    updateDecklineDriverActiveState,
    updateDecklineDenominatorDriverActiveState,
    selectedDecklineDriverOptions,
    decklineType,
    setDecklineType,
    selectedDenominatorDriverOptions,
    denominatorType,
    setDenominatorType,
    subtotalMap,
    setIsCreatingSubtotal,
    updateDecklinesForPageDriverChanges,
    isLoading: isDecklinesLoading,
  } = useDecklinesContext();
  const {
    isLoading,
    currentPageDriverOptions,
    modifiedPageProperties,
    updatePageDriverActiveState,
    pageRowData,
    pageLockStatus,
    configuredDatasets,
    pageLayoutOverrides,
    setPageLayoutOverrides,
    currentGranularityPage,
  } = usePageContext();

  const { hasPermissionLevel } = useUserContext();
  const isDevTeamAdmin = hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN);

  // Cutover switch: while OFF (backend not yet accepting calc_members on SaveDeck), the calc panel
  // writes the baked kpi_id/kpi_formula and the save omits calc_members (handoff §8 step 3).
  const { featureFlags } = useAurigaContext();
  const enableCalcMembersSave = featureFlags.enableCalcMembersSave;

  const { page_drivers: pageDriverSets, denominator_label: denominatorLabel } = modifiedPageProperties || {};

  // Force re-render when pageRowData changes
  useEffect(() => {}, [pageRowData]);

  const {
    calculateSubtotal,
    handleStartRowChange,
    handleEndRowChange,
    startInputRef,
    endInputRef,
    highlightedRange,
    showUnsavedDialog,
    setShowUnsavedDialog,
    checkUnsavedChanges,
    discardUnsavedChanges,
    applySubtotalRangeHighlighting,
  } = useSubtotalHelper(pageRowData);

  const [activeTab, setActiveTab] = useState<PanelTab>(DriverType.PAGE);
  const [localKpiName, setLocalKpiName] = useState<string>('');
  const [, setLocalSelectedDecklineId] = useState<string | null>(null);
  // Lifted from VariantOverridesDisplay so collapse state persists across deckline selections
  const [showInheritedDimensions, setShowInheritedDimensions] = useState(false);
  // Lifted from PageSettingsPanel so the Page Settings selection persists across driver tab switches
  const [pageSettingsView, setPageSettingsView] = useState<PageSettingsView | null>(null);

  const kpiLibraryRef = useRef<KPILibraryHandle>(null);
  const {
    pendingCreations: { allRootKPIs, getEnhancedVariants },
  } = useKPILibraryContext();

  // Fetch variants for the selected deckline's root KPI (hits React Query cache)
  const { data: fetchedVariants = [], isFetching: isVariantsFetching } = useGetVariantsForRootKPI({
    root_kpi_id: selectedDeckline?.root_kpi_id ?? '',
    options: { enabled: !!selectedDeckline?.root_kpi_id },
  });

  /** The selected deckline's variant, enhanced with isNew status */
  const selectedEnhancedVariant = useMemo(() => {
    if (!selectedDeckline?.root_kpi_id || !selectedDeckline?.kpi_variant_id) return null;
    const enhanced = getEnhancedVariants(selectedDeckline.root_kpi_id, fetchedVariants);
    return enhanced.find((v) => v.kpi_variant_id === selectedDeckline.kpi_variant_id) ?? null;
  }, [selectedDeckline?.root_kpi_id, selectedDeckline?.kpi_variant_id, getEnhancedVariants, fetchedVariants]);

  const {
    driverDisplayOptions: newVariantDriverOptions,
    placeholderDrivers: newVariantPlaceholderDrivers,
    onDriverChange: onNewVariantDriverChange,
    onDriverToggle: onNewVariantDriverToggle,
    changeVariantDataset,
  } = useVariantDriverSync({ selectedEnhancedVariant, pageDriverSets });

  // When data is loading, ensures the "Page Drivers" tab is selected — except the Admin tab, which is
  // independent of the page's data-load state (applying an override triggers a re-fetch, and the admin
  // should stay on the Admin tab through it rather than being bumped to Page Drivers mid-preview).
  useEffect(() => {
    if ((isLoading || isDecklinesLoading) && activeTab !== ADMIN_TAB) {
      setActiveTab(DriverType.PAGE);
    }
  }, [isDecklinesLoading, isLoading, activeTab]);

  // The Admin tab only exists for DEV_TEAM_ADMIN; if the tab is somehow active without that permission
  // (e.g. permission changes), fall back to Page Drivers so no one is stranded on a hidden tab.
  useEffect(() => {
    if (!isDevTeamAdmin && activeTab === ADMIN_TAB) {
      setActiveTab(DriverType.PAGE);
    }
  }, [isDevTeamAdmin, activeTab]);

  // Calculate start and end row values for display
  const getSubtotalRowValues = useCallback(() => {
    let startRow = selectedDeckline?.subtotal_start_index || '';
    let endRow = selectedDeckline?.subtotal_end_index || '';

    // If values are empty but this is a subtotal row, calculate from subtotalMap
    if (selectedDeckline && !isNil(selectedDeckline.subtotal_formula) && (!startRow || !endRow)) {
      const subtotalKey = selectedDeckline.deckline_id;
      const childRows = subtotalMap?.get(subtotalKey!);
      if (childRows && Array.isArray(childRows) && childRows.length > 0) {
        const rowNumbers = childRows.map((row) => Number(row.rowNumber)).filter((n) => !Number.isNaN(n));
        if (rowNumbers.length > 0) {
          startRow = Math.min(...rowNumbers).toString();
          endRow = Math.max(...rowNumbers).toString();
        }
      }
    }

    return { startRow, endRow };
  }, [selectedDeckline, subtotalMap]);

  // Apply highlighting when a subtotal row is clicked
  useEffect(() => {
    // Skip if user is actively editing the range (highlightedRange exists)
    if (highlightedRange) return;

    if (selectedDeckline && !isNil(selectedDeckline.subtotal_formula) && pageRowData) {
      const { startRow, endRow } = getSubtotalRowValues();
      if (startRow && endRow) {
        applySubtotalRangeHighlighting(Number(startRow), Number(endRow), pageRowData);
      }
    }
  }, [selectedDeckline, applySubtotalRangeHighlighting, getSubtotalRowValues, pageRowData, highlightedRange]);

  const { startRow, endRow } = getSubtotalRowValues();

  // Sync local state when selectedDeckline changes (only when switching decklines)
  useEffect(() => {
    const currentKpiName = selectedDeckline?.kpi_name ?? '';
    setLocalKpiName(currentKpiName);

    // NOTE: ESLint expects `selectedDeckline` as the dependency, but only want this to run when internalId changes.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedDeckline?.metadata?.internalId]);

  /**
   * When the deckline type changes, we need to ensure the denominator tab is not selected
   * if the deckline type is not compatible with denominators.
   */
  useEffect(() => {
    const excludeDenominator = EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(decklineType);

    setActiveTab((prev) => {
      // If denominator tab is selected but no longer supported, fall back to deckline tab
      if (prev === DriverType.DENOMINATOR_FOR_PERCENT && excludeDenominator) {
        return DriverType.DECKLINE;
      }

      return prev;
    });
  }, [decklineType]);

  /**
   * Dropdown options for Deckline types (Blank Line, Base KPI, Calculated KPI, etc.)
   */
  const pageSidePanelDropDown = useMemo(
    () => [
      {
        value: PageSidePanelDropDown.BLANK_LINE as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BLANK_LINE,
        id: 'blank-line',
      },
      {
        value: PageSidePanelDropDown.ADJUSTMENT as PageSidePanelDropDown,
        label: PageSidePanelDropDown.ADJUSTMENT,
        id: 'adjustment',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_ADJUSTMENT as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_ADJUSTMENT,
        id: 'calculated-adjustment',
      },
      {
        value: PageSidePanelDropDown.BASE_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BASE_KPI,
        id: 'base-kpi',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_KPI,
        id: 'calculated-kpi',
      },
      {
        value: PageSidePanelDropDown.SUBTOTAL as PageSidePanelDropDown,
        label: PageSidePanelDropDown.SUBTOTAL,
        id: 'subtotal',
        disabled: decklineType !== PageSidePanelDropDown.BLANK_LINE && decklineType !== PageSidePanelDropDown.SUBTOTAL,
      },
    ],
    [decklineType],
  );

  const denominatorTypeDropDown = useMemo(
    () => [
      {
        value: PageSidePanelDropDown.NONE,
        label: 'Not Applicable',
        id: 'not-applicable',
      },
      {
        value: PageSidePanelDropDown.BASE_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BASE_KPI,
        id: 'base-kpi',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_KPI,
        id: 'calculated-kpi',
      },
    ],
    [],
  );

  /**
   * Changes the active tab when selectedDeckline changes.
   */
  useEffect(() => {
    // Set the correct tab based on when selectedDeckline changes
    // We track internalId changes because the selectedDeckline may update from page driver changes.
    setLocalSelectedDecklineId((prev) => {
      if (selectedDeckline?.metadata.internalId !== prev) {
        setActiveTab((prevTab) => {
          if (isNil(selectedDeckline)) {
            return DriverType.PAGE;
          }

          // If we have a selectedDeckline and we're NOT already on a deckline-specific tab, switch to Deckline.
          if (prevTab !== DriverType.DECKLINE && prevTab !== DriverType.DENOMINATOR_FOR_PERCENT) {
            return DriverType.DECKLINE;
          }

          return prevTab;
        });
        return selectedDeckline?.metadata.internalId ?? null;
      }
      return prev;
    });
  }, [selectedDeckline]);

  /**
   * Handles the change in driver selection between Page and Deckline
   * @param _event - The event that triggered the change
   * @param value - The new value of the driver selection
   */
  const handleDriverTabChange = useCallback(
    (_event: SyntheticEvent, value: string) => {
      const newTab = value as PanelTab;
      if (newTab !== null) {
        if (checkUnsavedChanges()) {
          return; // Don't change tab if there are unsaved changes
        }
        setActiveTab(newTab);
      }
    },
    [checkUnsavedChanges],
  );

  /**
   * Handles KPI Selection , Subtotal , Adjustment , Blank Line
   * @param event - The select event which includes the PageSidePanelDropDown as a value
   */
  const handleKPISelection = useCallback(
    (event: SelectChangeEvent<PageSidePanelDropDown>) => {
      const newType = event.target.value as PageSidePanelDropDown;
      setDecklineType(newType);
      setIsCreatingSubtotal(newType === PageSidePanelDropDown.SUBTOTAL);
    },
    [setDecklineType, setIsCreatingSubtotal],
  );

  /**
   * Handles KPI Selection , Subtotal , Adjustment , Blank Line
   * @param event - The select event which includes the PageSidePanelDropDown as a value
   */
  const handleDenominatorTypeSelection = useCallback(
    (event: SelectChangeEvent<PageSidePanelDropDown>) => {
      const newType = event.target.value as PageSidePanelDropDown;
      setDenominatorType(newType);
    },
    [setDenominatorType],
  );

  /**
   * Handles updates to the Deckline Driver segments
   * @param key - The index of the segment in the segmentData array
   * @param segmentData - The segment data object
   * @param updates - The updates to be applied to the segment data
   * @param newKpiId - The new KPI ID
   */
  /**
   * Handles updates to the Denominator Driver segments
   * @param key - The index of the segment in the segmentData array
   * @param segmentData - The segment data object
   * @param updates - The updates to be applied to the segment data
   * @param newKpiId - The new KPI ID
   */
  const handleDenominatorDriverUpdate = useCallback(
    (_key: KpiDimension, updates: Partial<GalaxyKPIDrivers>, updatedDriverMap: DriverDisplayOptionMap) => {
      if (!selectedDeckline) {
        return;
      }

      const newData = {
        ...selectedDeckline.denominator_kpi_id_drivers,
        ...updates,
      } as GalaxyKPIDrivers;

      updatePageDeckline(selectedDeckline.metadata.internalId, 'denominator_kpi_id_drivers', newData);
      updatePageDeckline(
        selectedDeckline.metadata.internalId,
        'denominator_kpi_id_for_pct',
        generateBaseKpiId(updatedDriverMap, rootNodes),
      );
    },
    [selectedDeckline, updatePageDeckline, rootNodes],
  );

  /**
   * Adapts a `CalcExpressionInput` (what the panel emits) to the served `CalcExpression` shape that
   * `EnhancedDeckline.calc_members` holds in session.
   *
   * The served `CalcToken`/`CalcPin` output types do not DECLARE `hierarchy`, but we carry it through
   * at runtime anyway (cast): the authoring path resolves each node's hierarchy, and the save transform
   * in `DecklinesContext` reads it straight off these tokens/pins. Dropping it here is exactly the bug
   * that made the backend's driver-completeness gate fail — the hierarchy must survive to SaveDeck.
   */
  const asCalcExpression = useCallback((input: CalcExpressionInput): CalcExpression => {
    // The served output types omit `hierarchy` (on tokens/pins) and `dataset_source` (on the
    // expression); we attach them at runtime (cast) so the save transform can read them. The authored
    // `dataset_source` is the page's resolved dataset and MUST survive to the save — re-deriving it
    // from the calc row's (always-null) `deckline_drivers` would wrongly yield GALAXY on an HCTR page
    //. `NonNullable<...[number]>` is the element type of the output array.
    type TokenOut = NonNullable<NonNullable<CalcExpression['tokens']>[number]>;
    type PinOut = NonNullable<NonNullable<CalcExpression['pins']>[number]>;
    return {
      __typename: 'CalcExpression',
      dimension: input.dimension,
      ...(input.dataset_source ? { dataset_source: input.dataset_source } : {}),
      tokens: input.tokens.map(
        (token) =>
          ({
            __typename: 'CalcToken',
            kind: token.kind,
            value: token.value,
            ...(token.hierarchy ? { hierarchy: token.hierarchy } : {}),
          }) as TokenOut,
      ),
      pins: (input.pins ?? []).map(
        (pin) =>
          ({
            __typename: 'CalcPin',
            dimension: pin.dimension,
            node: pin.node,
            ...(pin.hierarchy ? { hierarchy: pin.hierarchy } : {}),
          }) as PinOut,
      ),
    } as CalcExpression;
  }, []);

  /**
   * Handles updates to the Calculated KPI expression.
   *
   * Cutover-gated (handoff §8 step 3): when `enableCalcMembersSave` is ON, persist the authored
   * `calc_members` and let the backend derive `kpi_id`/`kpi_formula` per column (design §4.2). When
   * OFF (today — the backend's SaveDeck input does not yet accept `calc_members`), this is a no-op and
   * the baked `kpi_id`/`kpi_formula` written by {@link handleCalculatedKpiLegacyChange} are saved
   * instead.
   * @param calcMembers - The serialized expression
   */
  const handleCalcMembersChange = useCallback(
    (calcMembers: CalcExpressionInput) => {
      if (!selectedDeckline || !enableCalcMembersSave) {
        return;
      }
      updatePageDeckline(selectedDeckline.metadata.internalId, 'calc_members', asCalcExpression(calcMembers));
    },
    [asCalcExpression, enableCalcMembersSave, selectedDeckline, updatePageDeckline],
  );

  /**
   * Legacy baked-id write for the Calculated KPI flow, used while `enableCalcMembersSave` is OFF: the
   * panel's generated `kpi_id`/`kpi_formula` are persisted (plus `is_calculated_kpi: Y`) so SaveDeck
   * sends the same shape as before the `calc_members` work. A no-op when the flag is ON (the backend
   * then derives these from `calc_members`).
   * @param newId - The generated kpi_id
   * @param newFormula - The generated kpi_formula
   */
  const handleCalculatedKpiLegacyChange = useCallback(
    (newId: string, newFormula: string) => {
      if (!selectedDeckline || enableCalcMembersSave) {
        return;
      }
      const { internalId } = selectedDeckline.metadata;
      updatePageDeckline(internalId, 'kpi_id', newId);
      updatePageDeckline(internalId, 'kpi_formula', newFormula);
      updatePageDeckline(internalId, 'is_calculated_kpi', CalculatedKPIValues.Y);
    },
    [enableCalcMembersSave, selectedDeckline, updatePageDeckline],
  );

  /**
   * Denominator Calculated KPI write — ALWAYS the legacy baked-id path, independent of
   * `enableCalcMembersSave`. The denominator has no dedicated `denominator_calc_members` field, and it
   * MUST NOT write the numerator's `calc_members` (a shared-field write would corrupt the numerator on
   * a deckline that has both). So the denominator keeps persisting `denominator_kpi_id_for_pct` /
   * `denominator_kpi_formula` (+ `denominator_is_calculated_kpi: Y`) as before — even when the flag is
   * ON for the numerator. A dedicated backend field is tracked separately (Taskei); until it exists
   * the denominator never takes the calc_members path.
   * @param newId - The generated denominator kpi_id
   * @param newFormula - The generated denominator kpi_formula
   */
  const handleDenominatorCalculatedKpiLegacyChange = useCallback(
    (newId: string, newFormula: string) => {
      if (!selectedDeckline) {
        return;
      }
      const { internalId } = selectedDeckline.metadata;
      updatePageDeckline(internalId, 'denominator_kpi_id_for_pct', newId);
      updatePageDeckline(internalId, 'denominator_kpi_formula', newFormula);
      updatePageDeckline(internalId, 'denominator_is_calculated_kpi', CalculatedKPIValues.Y);
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Handles toggling the active state of a segment
   * @param index - The index of the segment in the segmentData array
   */
  const handleSwitchToggle = useCallback(
    (key: KpiDimension, checked: boolean) => {
      if (activeTab === DriverType.PAGE) {
        updatePageDriverActiveState(key, checked, updateDecklinesForPageDriverChanges);
      } else if (activeTab === DriverType.DECKLINE) {
        updateDecklineDriverActiveState(key, checked);
      } else {
        // Deckline Denominator
        updateDecklineDenominatorDriverActiveState(key, checked);
      }
    },
    [
      activeTab,
      updatePageDriverActiveState,
      updateDecklinesForPageDriverChanges,
      updateDecklineDriverActiveState,
      updateDecklineDenominatorDriverActiveState,
    ],
  );

  /**
   * Handles changing the dataset (Source) of a new (pending) variant via the Source selector.
   * @param event - The Source Select change event carrying the new DatasetSource
   */
  const handleSourceChange = useCallback(
    (event: SelectChangeEvent<DatasetSource>) => {
      changeVariantDataset(event.target.value as DatasetSource);
    },
    [changeVariantDataset],
  );

  // To determine if the Page data is still loading or not
  const dataLoaded = !isLoading && !isEmpty(currentPageDriverOptions);

  /**
   * The dataset a Calculated-KPI row belongs to: the page's FIRST configured dataset (from
   * `page_drivers`). A Calc-KPI row is a formula over the PAGE's dimension space and has no independent
   * dataset identity, so its deckline drivers are deliberately NOT consulted — a new row has none (or
   * inherited Galaxy-shaped values), and `datasetSourceFromDrivers(null)` would default to GALAXY,
   * wrongly showing Galaxy dimensions on an HCTR-only page. Defaults to GALAXY when nothing configured.
   */
  const calcKpiDatasetSource = configuredDatasets[0] ?? DatasetSource.GALAXY;

  /**
   * Page-driver options for the Calc-KPI panel: the entry for {@link calcKpiDatasetSource}. Empty map
   * as the last resort so the panel renders rather than crashing on a missing entry.
   *
   * NOTE: preserves pre-refactor behavior on single-dataset Galaxy pages. The Calc-KPI `calc_members`
   * handoff (which replaces options-driven availability entirely) is a separate workstream, out of
   * scope here.
   */
  const calcKpiPageDriverOptions = useMemo<DriverDisplayOptionMap>(
    () => currentPageDriverOptions?.[calcKpiDatasetSource] ?? {},
    [currentPageDriverOptions, calcKpiDatasetSource],
  );

  /**
   * The page's headerTree layout, used by the Calc-KPI panel to compute the column-owned and
   * open-dimension sets. Keyed by `pageId` AND the DEV_TEAM_ADMIN preview overrides
   * (`deckCycle`/`publishYear`/`asOfDate`) exactly as `Page.tsx` passes them, so under an active
   * preview this stays a React-Query cache hit on the SAME key — not a separate fetch of the default
   * cycle whose column-owned set would differ from the rendered layout.
   *
   * TODO: Centralize this layout query in `PageContext` so `Page.tsx`, this panel, and
   * `PageAdminPanel` share one source of truth instead of each reconstructing the key — see
   * https://taskei.amazon.dev/tasks/52121ee0-91ec-4b31-b3c3-c9997eefc871
   */
  const { data: calcKpiPageLayout } = useGetPageLayout({
    pageId: currentGranularityPage?.pk ? entityKeyAsId(currentGranularityPage.pk) : undefined,
    deckCycle: pageLayoutOverrides.deckCycle,
    publishYear: pageLayoutOverrides.publishYear,
    asOfDate: pageLayoutOverrides.asOfDate,
    options: { enabled: !isNil(currentGranularityPage?.pk) },
  });

  /**
   * Renders the segment display list for Base KPI
   */
  const renderDenominatorDriverDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }
    if (!isNull(selectedDenominatorDriverOptions)) {
      return (
        <KPIDriversDisplay
          datasetSource={datasetSourceFromDrivers(selectedDeckline?.deckline_drivers)}
          driverOptions={selectedDenominatorDriverOptions}
          renderMode='dimensionPicker'
          onToggleSwitch={handleSwitchToggle}
          onChange={handleDenominatorDriverUpdate}
          disabled={pageLockStatus.isEditingDisabled}
        />
      );
    }

    return null;
  }, [
    handleDenominatorDriverUpdate,
    handleSwitchToggle,
    selectedDeckline,
    selectedDenominatorDriverOptions,
    pageLockStatus.isEditingDisabled,
  ]);

  const renderDenominatorCalculatedKpiDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <CalculatedKPIPanel
        key={`calc-denominator-${selectedDeckline.metadata.internalId}`}
        initialFormula={selectedDeckline.denominator_kpi_formula!}
        initialKpiId={selectedDeckline.denominator_kpi_id_for_pct}
        pageDrivers={calcKpiPageDriverOptions}
        pageLayout={calcKpiPageLayout}
        datasetSource={calcKpiDatasetSource}
        onFormulaChange={handleDenominatorCalculatedKpiLegacyChange}
        alwaysInclude={[KpiDimension.ACCOUNT]}
        disabled={pageLockStatus.isEditingDisabled}
      />
    );
  }, [
    calcKpiPageDriverOptions,
    calcKpiPageLayout,
    calcKpiDatasetSource,
    handleDenominatorCalculatedKpiLegacyChange,
    pageLockStatus.isEditingDisabled,
    selectedDeckline,
  ]);

  /**
   * Renders the Calculated KPI panel for the selected Deckline
   */
  const renderCalculatedKpiSegmentDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <CalculatedKPIPanel
        key={`calc-numerator-${selectedDeckline.metadata.internalId}`}
        initialFormula={selectedDeckline.kpi_formula!}
        initialKpiId={selectedDeckline.kpi_id}
        calcMembers={selectedDeckline.calc_members}
        pageDrivers={calcKpiPageDriverOptions}
        pageLayout={calcKpiPageLayout}
        datasetSource={calcKpiDatasetSource}
        onFormulaChange={handleCalculatedKpiLegacyChange}
        onCalcMembersChange={handleCalcMembersChange}
        disabled={pageLockStatus.isEditingDisabled}
      />
    );
  }, [
    calcKpiPageDriverOptions,
    calcKpiPageLayout,
    calcKpiDatasetSource,
    handleCalcMembersChange,
    handleCalculatedKpiLegacyChange,
    pageLockStatus.isEditingDisabled,
    selectedDeckline,
  ]);

  /**
   * Renders the Deckline Adjustment Panel Display
   */
  const renderDecklineAdjustmentDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        <ListItem disablePadding>
          <ListItemText
            primary='Adjusting Definition'
            secondary={selectedDeckline.kpi_id}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    );

    // return <TextField fullWidth label='Add Adjusting definition' margin='normal' className='text-field-background' />;
  }, [selectedDeckline]);

  /**
   * Renders the Deckline Subtotal Panel Display
   */
  const renderDecklineSubtotalDisplay = useCallback(() => {
    return (
      <Box>
        <Box className='subtotal'>
          <TextField
            key={`start-${selectedDeckline?.metadata?.internalId}-${startRow}`}
            label='Start Row'
            margin='normal'
            className='subtotal-text-field'
            defaultValue={startRow}
            onChange={handleStartRowChange}
            inputRef={startInputRef}
            disabled={pageLockStatus.isEditingDisabled}
          />
          <Box className='separator-container'> - </Box>
          <TextField
            key={`end-${selectedDeckline?.metadata?.internalId}-${endRow}`}
            label='End Row'
            margin='normal'
            className='subtotal-text-field'
            defaultValue={endRow}
            onChange={handleEndRowChange}
            inputRef={endInputRef}
            disabled={pageLockStatus.isEditingDisabled}
          />
        </Box>
        <Box className='subtotal-button-container'>
          <Button
            variant='contained'
            onClick={() => {
              calculateSubtotal();
              setIsCreatingSubtotal(false);
            }}
            color='primary'
          >
            Save Subtotal
          </Button>
        </Box>
      </Box>
    );
  }, [
    selectedDeckline?.metadata?.internalId,
    startRow,
    handleStartRowChange,
    startInputRef,
    endRow,
    handleEndRowChange,
    endInputRef,
    calculateSubtotal,
    setIsCreatingSubtotal,
    pageLockStatus.isEditingDisabled,
  ]);

  /**
   * Handles updated the Deckline Name (KPI Name).
   */
  const handleDecklineNameChange = useCallback(
    (name: string) => {
      const newValue = name.trimStart();
      setLocalKpiName((prev) => {
        if (selectedDeckline && newValue !== prev) {
          setTimeout(() => {
            updatePageDeckline(selectedDeckline.metadata.internalId, 'kpi_name', newValue);
          }, 20);
          return newValue;
        }
        return prev;
      });
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Handles Linked KPI changes (Root KPI and Variant selections).
   * Updates root_kpi_id, kpi_variant_id, and deckline_drivers on the selected deckline.
   *
   * Three mutually exclusive driver-initialization paths:
   * - Existing variant: merges variant_overrides onto page_drivers
   * - New (pending) variant: starts with empty drivers for user to fill
   * - Variant cleared (null): resets drivers to page defaults
   */
  const handleLinkedKpiChange = useCallback(
    (rootKpi: LinkedKPIChangeItem, variant: EnhancedKPIVariant | null) => {
      if (!selectedDeckline) return;
      const { internalId } = selectedDeckline.metadata;

      if (rootKpi.id !== undefined) {
        updatePageDeckline(internalId, 'root_kpi_id', rootKpi.id);
      }
      if (rootKpi.name !== undefined) {
        updatePageDeckline(internalId, 'kpi_name', rootKpi.name ?? '');
        setLocalKpiName(rootKpi.name ?? '');
      }

      updatePageDeckline(internalId, 'kpi_variant_id', variant?.kpi_variant_id ?? null);

      // For existing variants, merge the variant's overrides onto the page-driver entry for the
      // variant's OWN dataset (so inherited dims resolve for Galaxy or HCTR alike), then write to
      // deckline_drivers. Both operands are dataset-shaped and assign to AnyKPIDrivers with no narrowing.
      if (variant && !variant.isNew) {
        const variantDataset = variant.dataset_source ?? datasetSourceFromDrivers(variant.variant_overrides);
        const datasetPageDrivers = readPageDrivers(pageDriverSets, variantDataset);
        const mergedDrivers: AnyKPIDrivers = {
          ...(datasetPageDrivers ?? {}),
          ...(variant.variant_overrides ?? {}),
        };
        updatePageDeckline(internalId, 'deckline_drivers', asKPIDrivers(mergedDrivers));
        // Base-row kpi_id is backend-derived (from the variant) and dropped on save — clear to '' (unset).
        updatePageDeckline(internalId, 'kpi_id', '');
      } else if (variant?.isNew) {
        // New variants start with empty drivers for user to fill
        updatePageDeckline(internalId, 'deckline_drivers', asKPIDrivers(EMPTY_KPI_DRIVERS));
        updatePageDeckline(internalId, 'kpi_id', '');
      } else {
        // Variant cleared — reset drivers to page defaults (no exception)
        updatePageDeckline(internalId, 'deckline_drivers', null);
        updatePageDeckline(internalId, 'kpi_id', '');
      }
    },
    [selectedDeckline, updatePageDeckline, pageDriverSets],
  );

  /**
   * Opens the KPI Library dialog. If a root_kpi_id is selected, opens in Variant View;
   * otherwise opens in Root KPI View. Applies the selection to the deckline on assign.
   */
  const openKPILibrary = useCallback(async () => {
    if (!selectedDeckline) return;
    const rootKpiId = selectedDeckline.root_kpi_id;
    const rootKPI = rootKpiId ? (allRootKPIs.find((kpi) => kpi.root_kpi_id === rootKpiId) ?? null) : null;
    const hasVariant = !!selectedDeckline.kpi_variant_id;
    let view: KPILibraryView | undefined;
    if (hasVariant) {
      view = KPILibraryView.EDIT_VARIANT_VIEW;
    } else if (rootKPI) {
      view = KPILibraryView.VARIANT_VIEW;
    }

    const selection = await kpiLibraryRef.current?.open(view);
    if (!selection) return;

    handleLinkedKpiChange(
      { id: selection.rootKPI.root_kpi_id, name: selection.rootKPI.root_kpi_name },
      { ...selection.variant, isNew: false },
    );
  }, [selectedDeckline, allRootKPIs, handleLinkedKpiChange]);

  /**
   * Renders the segment display list for Decklines using Base KPI mode.
   * - New (pending) variants: editable KPIDriversDisplay with dimension pickers
   * - Existing variants: read-only VariantOverridesDisplay with overrides/inherited sections
   */
  const renderDecklineDriverDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    // No variant selected yet — don't show driver display
    if (!selectedDeckline.kpi_variant_id) {
      return null;
    }

    // The row's dataset comes from its linked variant (fallback: the deckline's own drivers).
    const decklineDataset =
      selectedEnhancedVariant?.dataset_source ?? datasetSourceFromDrivers(selectedDeckline.deckline_drivers);
    // Options are the datasets the page is configured for (its page_drivers entries); include the row's
    // own dataset defensively. Editable only for a new (pending) variant — an existing variant's dataset
    // is immutable, so it renders read-only.
    const sourceOptions = configuredDatasets.includes(decklineDataset)
      ? configuredDatasets
      : [decklineDataset, ...configuredDatasets];
    const isSourceEditable = !!selectedEnhancedVariant?.isNew && !pageLockStatus.isEditingDisabled;

    const sourceSelect = (
      // Box carries the 8px horizontal inset (matching .variant-overrides-display); the fullWidth
      // control fills it, so it aligns with the dimension fields below.
      <Box className='deckline-source-select'>
        <FormControl size='small' fullWidth margin='dense'>
          <InputLabel id='deckline-source-label'>Source</InputLabel>
          <Select
            labelId='deckline-source-label'
            value={decklineDataset}
            label='Source'
            size='small'
            disabled={!isSourceEditable}
            onChange={handleSourceChange}
          >
            {sourceOptions.map((ds) => (
              <MenuItem key={ds} value={ds}>
                {resolveDatasetSourceLabel(ds)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );

    // When selectedEnhancedVariant is null (still resolving), treat as existing — show read-only
    // until we confirm it's a new pending variant. Once resolved, isNew=true switches to pickers.
    let driverContent = null;
    if (!selectedEnhancedVariant?.isNew && selectedDecklineDriverOptions) {
      driverContent = (
        <VariantOverridesDisplay
          // Reuse the authoritative dataset (variant.dataset_source first) so the rendered dimension set
          // always matches the Source control — a variant with no/empty overrides won't fall back to Galaxy.
          datasetSource={decklineDataset}
          driverOptions={selectedDecklineDriverOptions}
          // Falls back to deckline_drivers to infer overrides while variant data is loading. Both fields
          // are `KPIDrivers | null`, which assign to the `AnyKPIDrivers` prop directly (no narrowing —
          // asGalaxyDrivers would drop an HCTR set).
          variantOverrides={selectedEnhancedVariant?.variant_overrides ?? selectedDeckline.deckline_drivers ?? null}
          showInherited={showInheritedDimensions}
          onToggleInherited={() => setShowInheritedDimensions((prev) => !prev)}
          // Opens KPI Library dialog to the variant edit view
          onFieldClick={openKPILibrary}
          disabled={isVariantsFetching || pageLockStatus.isEditingDisabled}
        />
      );
    } else if (!isNull(selectedDecklineDriverOptions)) {
      // New variant: editable dimension pickers bound to the pending variant (the authority), not the
      // deckline. Options/placeholder come from the variant's overrides + its dataset's page drivers.
      driverContent = (
        <KPIDriversDisplay
          className='deckline-drivers-display'
          datasetSource={decklineDataset}
          driverOptions={newVariantDriverOptions}
          placeholderDrivers={newVariantPlaceholderDrivers}
          renderMode='dimensionPicker'
          onToggleSwitch={onNewVariantDriverToggle}
          onChange={onNewVariantDriverChange}
          disabled={pageLockStatus.isEditingDisabled}
        />
      );
    }

    return (
      <>
        {sourceSelect}
        {driverContent}
      </>
    );
  }, [
    selectedDeckline,
    isVariantsFetching,
    selectedEnhancedVariant,
    selectedDecklineDriverOptions,
    configuredDatasets,
    showInheritedDimensions,
    newVariantDriverOptions,
    newVariantPlaceholderDrivers,
    onNewVariantDriverToggle,
    onNewVariantDriverChange,
    handleSourceChange,
    pageLockStatus.isEditingDisabled,
    openKPILibrary,
  ]);

  /**
   * Handles rendering the Deckline Toolbar (Deckline Name, Deckline Type, Formatting Controls)
   */
  const renderDecklineEditToolbar = useCallback(() => {
    if (!selectedDeckline) {
      return null;
    }
    return (
      <Box className='deckline-specific-section'>
        <FormControl size='small' fullWidth margin='dense'>
          <InputLabel id='deckline-type-label'>Type</InputLabel>
          <Select
            labelId='deckline-type-label'
            value={decklineType}
            onChange={handleKPISelection}
            label='Type'
            disabled={pageLockStatus.isEditingDisabled}
            size='small'
          >
            {pageSidePanelDropDown.map((option) => (
              <MenuItem key={option.id} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DecklineNameInput
          value={localKpiName}
          rootKpiId={selectedDeckline?.root_kpi_id}
          kpiVariantId={selectedDeckline?.kpi_variant_id}
          decklineType={decklineType}
          onNameChange={handleDecklineNameChange}
          onLinkedKpiChange={handleLinkedKpiChange}
          onOpenKPILibrary={openKPILibrary}
          newVariantDataset={resolveDefaultVariantDataset(pageDriverSets)}
          disabled={pageLockStatus.isEditingDisabled}
        />
        <Box className='deckline-formatting-controls'>
          <DecklineFormattingOptions isEditingDisabled={pageLockStatus.isEditingDisabled} />
        </Box>
      </Box>
    );
  }, [
    decklineType,
    handleDecklineNameChange,
    handleLinkedKpiChange,
    handleKPISelection,
    localKpiName,
    openKPILibrary,
    pageSidePanelDropDown,
    selectedDeckline,
    pageDriverSets,
    pageLockStatus.isEditingDisabled,
  ]);

  /**
   * Handles rendering the Denominator Edit Toolbar
   */
  const renderDenominatorEditToolbar = useCallback(() => {
    return (
      <Box className='deckline-specific-section denominator-section'>
        <Typography gutterBottom component='div'>
          <GenericFormControl
            value={denominatorType ?? PageSidePanelDropDown.NONE}
            onChange={handleDenominatorTypeSelection}
            options={denominatorTypeDropDown}
            disabled={pageLockStatus.isEditingDisabled}
            className='kpi-selector-dropdown'
          />
        </Typography>
      </Box>
    );
  }, [denominatorType, denominatorTypeDropDown, handleDenominatorTypeSelection, pageLockStatus.isEditingDisabled]);

  /**
   * Renders the content based on the active driver tab and KpiType for Deckline
   */
  const renderContent = useCallback(() => {
    // Admin preview controls are independent of the page's own data-load state — an admin may want to
    // pin layout overrides precisely because the current layout is empty or erroring.
    if (activeTab === ADMIN_TAB) {
      return <PageAdminPanel overrides={pageLayoutOverrides} onOverridesChange={setPageLayoutOverrides} />;
    }

    if (dataLoaded) {
      // Page Driver Content
      if (activeTab === DriverType.PAGE) {
        return <PageSettingsPanel selectedView={pageSettingsView} onViewChange={setPageSettingsView} />;
      }

      // Deckline Driver Content
      if (activeTab === DriverType.DECKLINE) {
        if (selectedDeckline) {
          return (
            <Box>
              {hasDriverMismatch(
                selectedDeckline?.deckline_drivers,
                readPageDrivers(pageDriverSets, datasetSourceFromDrivers(selectedDeckline?.deckline_drivers)),
              ) && (
                <Alert severity='warning' icon={false} className='exception-line'>
                  Exception
                </Alert>
              )}
              {/* Deckline Specific Toolbar */}
              {renderDecklineEditToolbar()}
              <Box className='page-side-panel-content-body'>
                {/* Deckline KpiType-specific Content */}
                {decklineType === PageSidePanelDropDown.BASE_KPI && renderDecklineDriverDisplay()}
                {decklineType === PageSidePanelDropDown.CALCULATED_KPI && renderCalculatedKpiSegmentDisplay()}
                {(decklineType === PageSidePanelDropDown.ADJUSTMENT ||
                  decklineType === PageSidePanelDropDown.CALCULATED_ADJUSTMENT) &&
                  renderDecklineAdjustmentDisplay()}
                {decklineType === PageSidePanelDropDown.SUBTOTAL && renderDecklineSubtotalDisplay()}
              </Box>
            </Box>
          );
        }
        // No deckline selected
        return <Typography className='page-side-panel-text-only'>Select a Deckline</Typography>;
      }

      // Denominator Driver Content ("% Gross Margin", "% Revenue", etc.)
      if (activeTab === DriverType.DENOMINATOR_FOR_PERCENT) {
        if (selectedDeckline) {
          return (
            <Box>
              {renderDenominatorEditToolbar()}

              {denominatorType === PageSidePanelDropDown.BASE_KPI && renderDenominatorDriverDisplay()}
              {denominatorType === PageSidePanelDropDown.CALCULATED_KPI && renderDenominatorCalculatedKpiDisplay()}
            </Box>
          );
        }

        // No deckline selected
        return <Typography className='page-side-panel-text-only'>Select a Deckline</Typography>;
      }

      // Unhandled activeTab value
      return <Typography className='page-side-panel-text-only'>Unhandled Driver Type: {activeTab}</Typography>;
    }

    // Data hasn't loaded
    return <Typography className='page-side-panel-text-only'>Display Drivers Loading...</Typography>;
  }, [
    dataLoaded,
    activeTab,
    pageSettingsView,
    selectedDeckline,
    pageDriverSets,
    renderDecklineEditToolbar,
    decklineType,
    renderDecklineDriverDisplay,
    renderCalculatedKpiSegmentDisplay,
    renderDecklineAdjustmentDisplay,
    renderDecklineSubtotalDisplay,
    renderDenominatorEditToolbar,
    denominatorType,
    renderDenominatorDriverDisplay,
    renderDenominatorCalculatedKpiDisplay,
    pageLayoutOverrides,
    setPageLayoutOverrides,
  ]);

  /**
   * Memoized TOC configuration items for the current Deck and Page View
   */
  const currTocSelectorConfig = useTocSelectorConfig(deckId, getTocItemsByGroup);

  /**
   * Determines if the current Deckline Type supports a Denominator
   */
  const isDenominatorSupported = !EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(decklineType);

  return (
    <Box>
      <TableOfContentsSelector
        deckId={deckId}
        tocPageItems={currTocSelectorConfig.currPageItems}
        viewType={ViewType.PAGE_VIEW}
        tocPageGroupLookup={currTocSelectorConfig.groupLookupValues}
      />
      <Box>
        <Tabs
          value={activeTab}
          onChange={handleDriverTabChange}
          variant='scrollable'
          scrollButtons='auto'
          aria-label='driver selection'
          className='driver-selector-tabs'
        >
          <Tab aria-label='page driver' label='Page Drivers' value={DriverType.PAGE} wrapped />
          <Tab aria-label='deckline driver' label='Deckline Drivers' value={DriverType.DECKLINE} wrapped />
          {denominatorLabel && isDenominatorSupported && (
            <Tab
              aria-label={DenominatorLabels[denominatorLabel]}
              label={DenominatorLabels[denominatorLabel]}
              value={DriverType.DENOMINATOR_FOR_PERCENT}
              wrapped
            />
          )}
          {isDevTeamAdmin && <Tab aria-label='admin' label='Admin' value={ADMIN_TAB} wrapped />}
        </Tabs>
      </Box>

      {/* Main Side Panel Content */}
      <Box className='page-side-panel-content'>{renderContent()}</Box>

      {/* Unsaved Subtotal Changes Dialog */}
      <ConfirmDialog
        isDialogOpen={showUnsavedDialog}
        setIsDialogOpen={setShowUnsavedDialog}
        dialogTitle='Unsaved Subtotal Changes'
        dialogText='You have unsaved subtotal changes. Do you want to save them or discard the changes?'
        confirmText='Save Subtotal'
        cancelText='Discard'
        handleConfirm={() => {
          calculateSubtotal();
          setIsCreatingSubtotal(false);
          setShowUnsavedDialog(false);
        }}
        handleCancel={discardUnsavedChanges}
      />
      <KPILibrary
        ref={kpiLibraryRef}
        rootKPI={
          selectedDeckline?.root_kpi_id
            ? (allRootKPIs.find((kpi) => kpi.root_kpi_id === selectedDeckline.root_kpi_id) ?? null)
            : null
        }
        variant={selectedEnhancedVariant}
        pageDrivers={asGalaxyDrivers(readPageDrivers(pageDriverSets, DatasetSource.GALAXY))}
      />
    </Box>
  );
};

export default PageSidePanel;
